import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import type { IExceptionTelemetry, ITraceTelemetry } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;

/**
 * Hosts that receive correlation headers (request-id / traceparent) on outgoing calls.
 *
 * Only our own API understands them: the .NET backend uses them to join browser and
 * server telemetry into one operation. Every other host must NOT get them. Third parties
 * such as Clerk reject the CORS preflight, and so does the App Insights ingestion endpoint
 * itself ("Request header field request-id is not allowed").
 *
 * This is an allowlist, not an exclude list, on purpose. PostHog session replay (network
 * capture) re-wraps window.fetch and rebuilds each Request, which drops the SDK's own
 * bypass flag on its ingestion POSTs. The ajax plugin then treats /v2/track like any other
 * dependency and adds request-id, and ingestion fails preflight for the whole session.
 * The SDK's regional *.in.applicationinsights.azure.com hosts are not in its internal
 * endpoint list, so no exclude list can be kept complete. With an allowlist, a host we
 * did not name never gets the headers. (Prod outage found 2026-10-26.)
 *
 * The SDK matches each entry as a regex against the request's host (including a
 * non-default port), so entries are plain hosts such as "api.example.com" or "localhost:7169".
 */
export const correlationHostsFrom = (urls: readonly (string | undefined)[]): string[] => {
  const hosts = new Set<string>();
  for (const url of urls) {
    if (!url) continue;
    try {
      const { host } = new URL(url);
      if (host) hosts.add(host.toLowerCase());
    } catch {
      // Relative base such as "/api" is same-origin; the SDK already correlates those.
    }
  }
  return [...hosts];
};

/**
 * @param connectionString App Insights connection string; no-op when unset.
 * @param apiBaseUrls Absolute base URLs of our own API(s). Only these hosts get correlation headers.
 */
export const initAppInsights = (
  connectionString: string | undefined,
  apiBaseUrls: readonly (string | undefined)[] = []
): void => {
  if (!connectionString) {
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      // Cross-origin correlation is needed because the API lives on another host,
      // but it is restricted to the hosts below. See correlationHostsFrom.
      enableCorsCorrelation: true,
      correlationHeaderDomains: correlationHostsFrom(apiBaseUrls),
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableTelemetry: false,
    },
  });

  appInsights.loadAppInsights();
};

export const setAppInsightsUser = (userId: string): void => {
  appInsights?.setAuthenticatedUserContext(userId);
};

export const clearAppInsightsUser = (): void => {
  appInsights?.clearAuthenticatedUserContext();
};

export const trackApiError = (
  status: number | null,
  url: string | undefined,
  method: string | undefined,
  message: string
): void => {
  if (!appInsights) return;

  const properties: Record<string, string> = {
    errorType: 'ApiError',
    url: url ?? 'unknown',
    method: method ?? 'unknown',
    message,
  };

  if (status !== null) {
    properties.httpStatus = String(status);
  }

  const severity =
    status === null || status >= 500
      ? SeverityLevel.Error
      : status === 401 || status === 403
        ? SeverityLevel.Warning
        : SeverityLevel.Information;

  const trace: ITraceTelemetry = {
    message: `API ${method ?? ''} ${url ?? ''} failed${status ? ` (${status})` : ''}: ${message}`,
    severityLevel: severity,
    properties,
  };

  appInsights.trackTrace(trace);
};

export const trackException = (error: Error, properties?: Record<string, string>): void => {
  if (!appInsights) return;

  const exception: IExceptionTelemetry = {
    exception: error,
    severityLevel: SeverityLevel.Error,
    properties,
  };

  appInsights.trackException(exception);
};

export const trackEvent = (name: string, properties?: Record<string, string>): void => {
  appInsights?.trackEvent({ name, properties });
};
